import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalReducer } from "../../GlobalContext";
import {
  getAttributeEntityNamesFromSearchEntity,
  getDefaultAdvancedNodesFromSearchEntity,
  getDefaultOrderedByFromSearchEntity,
} from "./searchDefaults";
import { getDefaultFieldsFromSearchEntity } from "./searchFields";
import { useSearchCatalogs } from "./searchCatalog";
import { isFailedResponse } from "../api/request";
import { searchRecords } from "./searchRecords";
import { MAX_SELECTED_RECORDS } from "../selection/tableSelection";
import {
  SearchColumn,
  SearchNode,
  SearchOrder,
  SearchQuery,
  SearchRecord,
  SearchRequestProperties,
  SearchSelection,
} from "./advancedSearch.types";
import {
  buildSearchRequest,
  buildSelectionRequest,
  getSearchJoins,
  validateQuery,
} from "./advancedSearchRequest";

const NoSelection: SearchSelection = { ids: [], allPages: false };

const defaultQuery = (entityName: string): SearchQuery => ({
  entityName,
  nodes: getDefaultAdvancedNodesFromSearchEntity(entityName),
  columns: getDefaultFieldsFromSearchEntity(entityName),
  order: {
    by: getDefaultOrderedByFromSearchEntity(entityName),
    asc: false,
    secondaryBy: "",
    secondaryAsc: false,
  },
});

export interface UseAdvancedSearchOptions {
  initialEntity: string;
  allowedEntityTypes?: Record<string, string> | null;
  scope?: SearchRequestProperties;
  initialPageSize?: number;
}

/**
 * An advanced search: the entity, rules, columns and order being built, the page of
 * results last submitted, and the records ticked in them. What is built is not read
 * until `submit`, so editing the rules never changes the results on screen.
 */
export function useAdvancedSearch({
  initialEntity,
  allowedEntityTypes,
  scope,
  initialPageSize = 10,
}: UseAdvancedSearchOptions) {
  const globalDispatch = useGlobalReducer();
  const [query, setQuery] = useState(() => defaultQuery(initialEntity));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState<SearchQuery | null>(null);
  const [records, setRecords] = useState<SearchRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState(NoSelection);
  const latestRead = useRef(0);
  // Every record read since the search was submitted, so ticks from other pages can be named.
  const seen = useRef(new Map<string, SearchRecord>());

  const requestOptions = useMemo(
    () => ({ allowedEntityTypes, scope }),
    [allowedEntityTypes, scope]
  );

  const fieldsReady = useSearchCatalogs(
    globalDispatch,
    getAttributeEntityNamesFromSearchEntity(
      [{ Key: "advancedSearchEntityName", Value: query.entityName }],
      getSearchJoins(query.entityName, allowedEntityTypes)
    )
  );

  // The defaults name fields metadata describes, so they are filled in once it has arrived.
  const defaultsFor = useRef("");

  useEffect(() => {
    if (!fieldsReady || defaultsFor.current === query.entityName) {
      return;
    }

    defaultsFor.current = query.entityName;
    setQuery((current) => ({
      ...current,
      columns: current.columns.length ? current.columns : getDefaultFieldsFromSearchEntity(current.entityName),
      nodes: current.nodes.length ? current.nodes : getDefaultAdvancedNodesFromSearchEntity(current.entityName),
    }));
  }, [fieldsReady, query.entityName]);

  const read = useCallback(
    async (toRead: SearchQuery, pageNumber: number, size: number) => {
      const reading = ++latestRead.current;
      setLoading(true);

      const response = await searchRecords(
        globalDispatch,
        "",
        buildSearchRequest(toRead, {
          ...requestOptions,
          page: pageNumber,
          pageSize: size,
        })
      );

      if (reading !== latestRead.current) {
        return;
      }

      const failed = isFailedResponse(response);
      const found = failed ? [] : response?.data?.Records ?? [];
      found.forEach((record) => seen.current.set(record.Id, record));
      setRecords(found);
      setTotal(failed ? 0 : response?.data?.TotalRecords ?? 0);
      setPage(pageNumber);
      setLoading(false);
    },
    [globalDispatch, requestOptions]
  );

  const clearResults = () => {
    latestRead.current++;
    seen.current.clear();
    setSubmitted(null);
    setRecords([]);
    setTotal(0);
    setPage(1);
    setLoading(false);
    setSelection(NoSelection);
  };

  const submit = () => {
    const problems = validateQuery(query);
    setErrors(problems);

    if (Object.keys(problems).length > 0) {
      return;
    }

    setSubmitted(query);
    setSelection(NoSelection);
    seen.current.clear();
    return read(query, 1, pageSize);
  };

  /** Back to the entity's defaults, with no results. */
  const reset = () => {
    setQuery(defaultQuery(query.entityName));
    setErrors({});
    clearResults();
  };

  const setEntity = (entityName: string) => {
    setQuery(defaultQuery(entityName));
    setErrors({});
    clearResults();
  };

  /** Replaces what is being built, as loading a saved view does; no results are shown. */
  const loadQuery = (loaded: SearchQuery) => {
    setQuery(loaded);
    setErrors({});
    clearResults();
  };

  const setNodes = (nodes: SearchNode[]) =>
    setQuery((current) => ({ ...current, nodes }));

  /** Once results are on screen, the new columns are read for them straight away. */
  const setColumns = (columns: SearchColumn[]) => {
    setQuery((current) => ({ ...current, columns }));

    if (submitted) {
      const next = { ...submitted, columns };
      setSubmitted(next);
      read(next, page, pageSize);
    }
  };

  /** Re-reads the submitted search in the new order, as a column header does. */
  const sortBy = (order: SearchOrder) => {
    setQuery((current) => ({ ...current, order }));

    if (submitted) {
      const next = { ...submitted, order };
      setSubmitted(next);
      read(next, 1, pageSize);
    }
  };

  const goToPage = (pageNumber: number) =>
    submitted && read(submitted, pageNumber, pageSize);

  const setPageSize = (size: number) => {
    setPageSizeState(size);

    if (submitted) {
      setSelection(NoSelection);
      read(submitted, 1, size);
    }
  };

  const toggleRecord = (id: string) =>
    setSelection((current) => ({
      allPages: false,
      ids: current.ids.includes(id)
        ? current.ids.filter((selected) => selected !== id)
        : [...current.ids, id],
    }));

  /** Ticks or unticks every record on the page shown. */
  const togglePage = (select: boolean) =>
    setSelection((current) => {
      const onPage = records.map((record) => record.Id);

      return {
        allPages: false,
        ids: select
          ? Array.from(new Set([...current.ids, ...onPage]))
          : current.ids.filter((id) => !onPage.includes(id)),
      };
    });

  const selectAllPages = () => setSelection({ ids: [], allPages: true });
  const clearSelection = () => setSelection(NoSelection);

  const selectedCount = selection.allPages ? total : selection.ids.length;

  /** The submitted search restricted to the selection, for an export or list change. */
  const getSelectionRequest = (extraScope?: SearchRequestProperties) =>
    submitted
      ? buildSelectionRequest(submitted, selection, {
          ...requestOptions,
          scope: { ...scope, ...extraScope },
        })
      : null;

  /**
   * The records an action that works from ids applies to. With all pages selected they
   * are read in one request, which the selection limit keeps to MAX_SELECTED_RECORDS.
   * Null if they could not be read.
   */
  const getRecordsForAction = async (): Promise<SearchRecord[] | null> => {
    if (!selection.allPages) {
      return selection.ids
        .map((id) => seen.current.get(id))
        .filter((record): record is SearchRecord => Boolean(record));
    }

    if (!submitted || selectedCount > MAX_SELECTED_RECORDS) {
      return null;
    }

    const response = await searchRecords(
      globalDispatch,
      "",
      buildSearchRequest(submitted, {
        ...requestOptions,
        page: 1,
        pageSize: MAX_SELECTED_RECORDS,
      })
    );

    return isFailedResponse(response) ? null : response?.data?.Records ?? null;
  };

  return {
    query,
    submitted,
    errors,
    fieldsReady,
    records,
    total,
    page,
    pageSize,
    loading,
    selection,
    selectedCount,
    withinSelectionLimit: selectedCount <= MAX_SELECTED_RECORDS,
    submit,
    reset,
    setEntity,
    loadQuery,
    setNodes,
    setColumns,
    sortBy,
    goToPage,
    setPageSize,
    toggleRecord,
    togglePage,
    selectAllPages,
    clearSelection,
    getSelectionRequest,
    getRecordsForAction,
    refresh: () => submitted && read(submitted, page, pageSize),
  };
}

export type AdvancedSearch = ReturnType<typeof useAdvancedSearch>;
